import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function ProfileAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Profile API" description="The Profile API provides comprehensive company fundamentals, market statistics,
          analyst recommendations, and corporate details. These endpoints deliver key
          financial metrics, va..."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/corp.-intelligence/00-profile" />

      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Profile API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The Profile API provides comprehensive company fundamentals, market statistics,
          analyst recommendations, and corporate details. These endpoints deliver key
          financial metrics, valuation data, earnings calendars, and executive information
          for publicly traded companies. All endpoints require a valid ticker symbol.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* ========== Get Asset Profile ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /profiles/:ticker
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns detailed company information including business summary, contact details,
                industry classification, executive leadership, and governance risk ratings.
                This endpoint combines the core asset profile with the current executive team.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Stock ticker symbol (e.g., &quot;AAPL&quot;, &quot;MSFT&quot;, &quot;NVDA&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/profiles/AAPL', '/profiles/MSFT']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">address1, city, state, zip, country</p>
                  <p className="text-sm text-gray-600">Corporate headquarters location</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">website, irWebsite</p>
                  <p className="text-sm text-gray-600">Company and investor relations URLs</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">sector, industry</p>
                  <p className="text-sm text-gray-600">Industry classification</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">longBusinessSummary</p>
                  <p className="text-sm text-gray-600">Detailed description of the company's operations</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">fullTimeEmployees</p>
                  <p className="text-sm text-gray-600">Number of employees</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">companyOfficers</p>
                  <p className="text-sm text-gray-600">List of executives with name, age, title, and totalPay</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">auditRisk, boardRisk, compensationRisk, shareHolderRightsRisk, overallRisk</p>
                  <p className="text-sm text-gray-600">Governance risk scores (lower is better)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Company Profile (Apple)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Company Profile (Apple)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="profile/ticker"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response (abbreviated)</p>
                <Code id="03-fundamentals/corp.-intelligence/00-profile"></Code>
              </div>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
}
